"use client";

/**
 * Enhanced authentication hook
 * Provides easy access to auth state, user information, and identity tokens for Solana
 * Based on Spicenet portal patterns adapted for Yiedl
 */

import { usePrivy } from "@privy-io/react-auth";
import { useWallets } from "@privy-io/react-auth/solana";
import { useAuthContext } from "@/contexts/auth-context";
import { useMemo } from "react";

export function useAuth() {
  const {
    user: privyUser,
    authenticated,
    ready,
    linkEmail,
    unlinkEmail,
    getAccessToken,
  } = usePrivy();
  const { wallets } = useWallets();
  const { login, logout } = useAuthContext();

  // Get the first (primary) Solana wallet
  const solanaWallet = useMemo(() => {
    if (!wallets || wallets.length === 0) return null;
    return wallets[0];
  }, [wallets]);

  // Extract wallet address
  const address = useMemo(() => {
    return solanaWallet?.address;
  }, [solanaWallet]);

  // Extract user email if available
  const email = useMemo(() => {
    return privyUser?.email?.address || null;
  }, [privyUser]);

  // Get auth token (memoized to avoid unnecessary calls)
  const token = useMemo(() => {
    if (!authenticated || !ready) return null;
    return getAccessToken();
  }, [authenticated, ready, getAccessToken]);

  return {
    // Authentication state
    isAuthenticated: authenticated && ready,
    isLoading: !ready,

    // User information
    user: privyUser,
    address,
    email,

    // Auth token for API calls
    token,

    // Solana wallet object
    wallet: solanaWallet,

    // All connected wallets
    wallets,

    // Auth methods
    login,
    logout,

    // Email management methods
    linkEmail,
    unlinkEmail,
  };
}
